import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Layer } from '@deck.gl/core';
import { useFlights, useHeatmap, useLive, useNoiseStats } from '../api/hooks';
import BaseMap from '../components/map/BaseMap';
import { aircraftLayer, heatmapLayer, trafficPathLayer } from '../components/map/layers';
import HealthStrip from '../components/HealthStrip';
import ScoreBadge from '../components/ScoreBadge';
import { Button, Card, CardHeader, Empty, Stat } from '../components/ui';
import { deadReckon, hafToMslM } from '../lib/geo';
import { fmtDuration, fmtTime } from '../lib/format';

const iso = (d: Date) => d.toISOString().slice(0, 10);
export default function Dashboard() {
  const { data: live } = useLive();
  const today = iso(new Date()), monthStart = `${today.slice(0, 7)}-01`;
  const { data: flights } = useFlights({ from: today, limit: 50 });
  const { data: noise } = useNoiseStats(monthStart, `${today}T23:59:59`);
  const [showHeat, setShowHeat] = useState(false);
  const [bearing, setBearing] = useState(0);
  const { data: heat } = useHeatmap({ from: monthStart, legs: 'downwind,final' });
  const nowTs = Math.floor(Date.now() / 1000);

  const layers: Layer[] = useMemo(() => {
    if (!live) return [];
    const fleet = live.aircraft.filter(a => a.pos && a.lastSeenTs !== null && nowTs - a.lastSeenTs < 900).map(a => {
      const dr = deadReckon(a.pos!, nowTs - a.lastSeenTs!);
      return { lon: dr.lon, lat: dr.lat, alt: hafToMslM(a.pos!.hAF), track: a.pos!.track, color: (a.airborne ? [34, 211, 238] : [156, 163, 175]) as [number, number, number], label: `${a.registration} ${Math.round(a.pos!.hAF)}ft` };
    });
    const traffic = live.traffic.filter(t => nowTs - t.ts < 120).map(t => ({ lon: t.lon, lat: t.lat, alt: (t.alt ?? 0) * 0.3048, track: t.track ?? 0, color: [156, 163, 175] as [number, number, number], label: t.reg ?? t.hex, alpha: 140 }));
    const byFlight = new Map<string, [number, number, number][]>();
    for (const p of live.trails) { const arr = byFlight.get(p.flight_id) ?? []; arr.push([p.lon, p.lat, p.on_ground ? hafToMslM(0) : (p.alt_baro ?? 26) * 0.3048]); byFlight.set(p.flight_id, arr); }
    return [
      ...(showHeat && heat ? [heatmapLayer(heat.points)] : []),
      trafficPathLayer('live-trails', [...byFlight.values()].map(path => ({ path, color: [34, 211, 238] as [number, number, number] }))),
      ...aircraftLayer('live-traffic', traffic, 18, bearing),
      ...aircraftLayer('live-fleet', fleet, 26, bearing),
    ];
  }, [live, nowTs, showHeat, heat, bearing]);

  return (
    <div className="flex-1 flex flex-col">
      <HealthStrip />
      <div className="flex-1 grid grid-cols-1 xl:grid-cols-[1fr_380px] min-h-0">
        <div className="relative min-h-[480px] overflow-hidden">
          <BaseMap layers={layers} onBearing={setBearing} />
          <div className="absolute left-3 top-3 z-10 flex gap-2">
            <Button size="sm" variant={showHeat ? 'primary' : 'default'} onClick={() => setShowHeat(!showHeat)}>fleet heatmap (month)</Button>
          </div>
          <div className="absolute left-3 bottom-8 z-10 hud text-xs bg-ink/80 border border-line rounded px-2 py-1.5 space-y-0.5">
            {live?.aircraft.map(a => <div key={a.registration} className="flex gap-3"><span className={a.airborne ? 'text-cyan' : 'text-dim'}>{a.registration}</span><span className="text-dim">{a.airborne ? `airborne · ${Math.round(a.pos?.hAF ?? 0)} ft · ${Math.round(a.pos?.gs ?? 0)} kt` : a.lastSeenTs ? `seen ${fmtTime(a.lastSeenTs)}` : 'not seen'}</span>{a.flightId && <Link to={`/flights/${a.flightId}`} className="text-cyan">open ↗</Link>}</div>)}
            {!live?.aircraft.length && <span className="text-dim">no aircraft tracked — add some under Aircraft</span>}
          </div>
        </div>
        <aside className="border-l border-line bg-panel overflow-y-auto">
          <div className="grid grid-cols-2 divide-x divide-line border-b border-line">
            <Stat label="Flights today" value={flights?.length ?? 0} />
            <Stat label="Noise (month)" value={noise?.violations ?? 0} sub={`${noise?.flights ?? 0} flights audited`} />
          </div>
          <Card className="m-3"><CardHeader title="Today" />
            {!flights?.length ? <Empty>No flights yet today.</Empty> : <ul className="divide-y divide-line text-sm">{flights.map(f => <li key={f.id} className="px-4 py-2 flex items-center gap-3"><Link to={`/flights/${f.id}`} className="hud hover:text-cyan">{fmtTime(f.startedAt)}</Link><span className="font-medium">{f.registration}</span><span className="text-dim text-xs">{f.status === 'open' ? 'in flight' : `${fmtDuration(f.durationS)} · ${f.circuitCount ?? 0} circuits`}</span><span className="ml-auto"><ScoreBadge score={f.overallScore} size="sm" /></span></li>)}</ul>}
          </Card>
          {noise && noise.byAircraft.length > 0 && <Card className="m-3"><CardHeader title="Noise by aircraft (month)" /><ul className="text-sm divide-y divide-line">{noise.byAircraft.map(r => <li key={r.registration} className="px-4 py-1.5 flex justify-between"><span>{r.registration}</span><span className="hud text-amber">{r.n}</span></li>)}</ul></Card>}
        </aside>
      </div>
    </div>
  );
}
